import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { EntitlementsRead } from '../../types/api';
import { formatPlanDate, PLAN_LABEL, upgradePath } from './planLabels';
import { usePlan } from './usePlan';

const DAY_MS = 24 * 60 * 60 * 1000;

/** The last days of a trial, when a dismissed banner comes back. */
const FINAL_DAYS = 2;

/**
 * Per tab: which stretch of the trial the user hid the banner for. Holding the
 * stretch, not a flag, is what brings it back on the final two days.
 */
const ACTIVE_DISMISS_KEY = 'outpost.trialBanner.dismissed';

/**
 * Per browser, keyed by the expiry: a later trial ends on another date, so its
 * "ended" line shows again.
 */
const ENDED_DISMISS_PREFIX = 'outpost.trialEndedBanner.dismissed:';

type Stretch = 'early' | 'final';

/** Storage can throw (private mode, a full quota, blocked cookies); treat as empty. */
function readStorage(storage: () => Storage, key: string): string | null {
  try {
    return storage().getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(storage: () => Storage, key: string, value: string): void {
  try {
    storage().setItem(key, value);
  } catch {
    // Not remembered; the banner still hides for this render.
  }
}

const session = () => window.sessionStorage;
const local = () => window.localStorage;

/** Whole days left, rounded up; 0 under a day ("ends today"). */
function daysLeft(expiresAt: string, now: number): number | null {
  const end = new Date(expiresAt).getTime();
  if (Number.isNaN(end)) return null;
  const remaining = end - now;
  return remaining < DAY_MS ? 0 : Math.ceil(remaining / DAY_MS);
}

/**
 * One line under the header about the app-side trial (L5).
 *
 * Active: "Solo trial · 5 days left · Upgrade", hidden for the tab on Dismiss
 * and back again on the last two days. Ended: "Your Solo trial ended on 20 Oct
 * · You're on Free · Upgrade" until dismissed, remembered per trial. Nothing
 * while the plan is unknown or there is no trial: it informs, it never gates.
 */
export function TrialBanner() {
  const { entitlements } = usePlan();
  if (!entitlements?.trial_state || !entitlements.plan_expires_at) return null;

  return entitlements.trial_state === 'active' ? (
    <ActiveTrialBanner entitlements={entitlements} expiresAt={entitlements.plan_expires_at} />
  ) : (
    <EndedTrialBanner entitlements={entitlements} expiresAt={entitlements.plan_expires_at} />
  );
}

function ActiveTrialBanner({
  entitlements,
  expiresAt,
}: {
  entitlements: EntitlementsRead;
  expiresAt: string;
}) {
  // Read once per mount: the count moves by days, and the shell remounts on reload.
  const [now] = useState(() => Date.now());
  const [dismissed, setDismissed] = useState(() => readStorage(session, ACTIVE_DISMISS_KEY));

  const days = daysLeft(expiresAt, now);
  if (days === null) return null;
  const stretch: Stretch = days <= FINAL_DAYS ? 'final' : 'early';
  if (dismissed === stretch) return null;

  function dismiss() {
    writeStorage(session, ACTIVE_DISMISS_KEY, stretch);
    setDismissed(stretch);
  }

  const left = days === 0 ? 'ends today' : days === 1 ? '1 day left' : `${days} days left`;

  return (
    <div className="trial-banner" role="status">
      <p className="trial-banner__text">
        <span>{PLAN_LABEL[entitlements.plan]} trial</span>
        <span aria-hidden="true">·</span>
        <span>{left}</span>
        <span aria-hidden="true">·</span>
        <Link className="trial-banner__link" to={upgradePath('solo')}>
          Upgrade
        </Link>
      </p>
      <DismissButton onDismiss={dismiss} />
    </div>
  );
}

function EndedTrialBanner({
  entitlements,
  expiresAt,
}: {
  entitlements: EntitlementsRead;
  expiresAt: string;
}) {
  const key = `${ENDED_DISMISS_PREFIX}${expiresAt}`;
  const [dismissed, setDismissed] = useState(() => readStorage(local, key) !== null);
  if (dismissed) return null;

  function dismiss() {
    writeStorage(local, key, '1');
    setDismissed(true);
  }

  return (
    <div className="trial-banner trial-banner--ended" role="status">
      <p className="trial-banner__text">
        <span>
          Your {PLAN_LABEL.solo} trial ended on {formatPlanDate(expiresAt, { year: false })}
        </span>
        <span aria-hidden="true">·</span>
        <span>You&rsquo;re on {PLAN_LABEL[entitlements.plan]}</span>
        <span aria-hidden="true">·</span>
        <Link className="trial-banner__link" to={upgradePath('solo')}>
          Upgrade
        </Link>
      </p>
      <DismissButton onDismiss={dismiss} />
    </div>
  );
}

function DismissButton({ onDismiss }: { onDismiss: () => void }) {
  return (
    <button
      className="trial-banner__close"
      type="button"
      aria-label="Dismiss trial notice"
      onClick={onDismiss}
    >
      ×
    </button>
  );
}
